import {
  InvoiceInfo,
  IssueAmountStructure,
  IssueCaseDoc,
  IssueProduct,
  IssueTask,
  IssueTaskCategory,
  IssueTaskDeadline,
  IssueTaskDeadlineOption,
  IssueTaskDeliveryInfo,
  IssueTaskDoc,
  IssueTaskExternalService,
  IssueTaskInventoryIssueInfo,
  IssueTaskPickupInfo,
  IssueTaskStatus,
} from '@gooduncles/gu-app-schema';
import { uniqBy } from 'lodash-es';
import { issueTaskCategoriesForTaskSpots, issueTaskStatusForTargetDate } from 'src/constants/issue.constants';

import {
  needDeadlineSetting,
  needDeliveryInfoSetting,
  needInventoryIssueInfoSetting,
  needInvoiceInfoSetting,
  needPickupInfoSetting,
} from 'src/utils/issue/issue-util';

import { formatDate } from './date-util';
import { getInvoice, getIssueTasks } from './firebase/firebase-short-cut';

/**
 * REFUND 카테고리일 때 상품 정보를 음수로 변환합니다.
 * quantity와 amount 모두 음수로 변환합니다.
 */
export const convertProductsToNegative = (products: IssueProduct[] | null): IssueProduct[] | null => {
  if (!products) return null;
  return products.map((product) => ({
    ...product,
    quantity: -Math.abs(product.quantity),
    amount: product.amount
      ? {
          totalAmount: -Math.abs(product.amount.totalAmount),
          supplyAmount: -Math.abs(product.amount.supplyAmount),
          taxAmount: -Math.abs(product.amount.taxAmount),
        }
      : null,
  }));
};

/**
 * REFUND 카테고리일 때 금액을 음수로 변환합니다.
 */
const convertAmountToNegative = (amount: IssueAmountStructure | null): IssueAmountStructure | null => {
  if (!amount) return null;
  return {
    totalAmount: -amount.totalAmount,
    supplyAmount: -amount.supplyAmount,
    taxAmount: -amount.taxAmount,
  };
};

/**
 * PICK_UP Task의 inventoryIssueInfo 상태를 검증합니다.
 *
 * @returns { isValid: false, errorMessage?: string } - 에러 상태 및 에러 메시지
 * @returns { isValid: true, data: {...} } - 정상 상태 및 검증된 데이터
 */
export const validateInventoryIssueInfo = (
  issueTaskDoc: Pick<IssueTask, 'products' | 'inventoryIssueInfo'>,
):
  | { isValid: false; errorMessage?: string }
  | {
      isValid: true;
      data: {
        products: NonNullable<IssueTask['products']>;
        inventoryIssueInfo: NonNullable<IssueTask['inventoryIssueInfo']>;
      };
    } => {
  const { products, inventoryIssueInfo } = issueTaskDoc;

  // 1. products가 없거나 비어있으면 표시하지 않음 (에러 아님, 단순히 표시 안함)
  if (!products || products.length === 0) {
    return { isValid: false };
  }

  // 2. inventoryIssueInfo가 없으면 에러
  if (!inventoryIssueInfo) {
    return {
      isValid: false,
      errorMessage: '재고 처리 정보가 없습니다.',
    };
  }

  // 3. inventoryIssueInfo.products가 비어있으면 데이터 불일치 에러
  const inventoryProductCount = Object.keys(inventoryIssueInfo.products).length;
  if (inventoryProductCount === 0) {
    return {
      isValid: false,
      errorMessage: '재고 처리 정보 동기화 오류',
    };
  }

  // 모든 검증 통과 - 검증된 데이터 반환
  return {
    isValid: true,
    data: {
      products,
      inventoryIssueInfo,
    },
  };
};

/**
 * PICK_UP Task의 inventoryIssueInfo.products를 새로운 products 배열과 동기화합니다.
 * 기존 처리 상태는 유지하고, 새로운 상품은 pending 상태로 초기화합니다.
 */
export const syncInventoryIssueInfoWithProducts = (
  existingInventoryIssueInfo: IssueTaskInventoryIssueInfo | null,
  newProducts: { productId: string }[],
): IssueTaskInventoryIssueInfo => {
  const existingProducts = existingInventoryIssueInfo?.products ?? {};
  const syncedProducts: IssueTaskInventoryIssueInfo['products'] = {};

  newProducts.forEach((product) => {
    // 기존 정보가 있으면 유지, 없으면 pending 상태로 초기화
    syncedProducts[product.productId] = existingProducts[product.productId] || {
      status: 'pending',
      inventoryIssueId: null,
      processedAt: null,
    };
  });

  return {
    products: syncedProducts,
  };
};

export type IssueTaskBase = Pick<IssueTask, 'creatorId' | 'creatorType' | 'category'>;
export const getIssueTaskInitialData = (issueCaseDoc: IssueCaseDoc, base: IssueTaskBase): IssueTask => {
  // 바로 처리하는 퀵 발송을 제외하고는 데드라인을 설정한다.
  const deadline: IssueTaskDeadline | null = needDeadlineSetting(base.category)
    ? {
        option: IssueTaskDeadlineOption.ASAP,
        value: null,
      }
    : null;

  // 배송 이슈인 경우 배송 정보를 설정한다.
  const deliveryInfo: IssueTaskDeliveryInfo | null = needDeliveryInfoSetting(base.category)
    ? {
        partnersTaskId: null,
        deliveryPartnerId: null,
        deliveryPartnerName: null,
        deliverySpotId: null,
        deliveryTaskFinishedAt: null,
        storeId: issueCaseDoc.storeId,
        storeName: issueCaseDoc.storeName,
        memo: null,
        images: null,
      }
    : null;

  // 픽업 이슈인 경우 픽업 정보를 설정한다.
  const pickupInfo: IssueTaskPickupInfo | null = needPickupInfoSetting(base.category)
    ? {
        partnersTaskId: null,
        pickupPartnerId: null,
        pickupPartnerName: null,
        pickupTaskFinishedAt: null,
        storeId: issueCaseDoc.storeId,
        storeName: issueCaseDoc.storeName,
        memo: null,
        images: null,
      }
    : null;

  const invoiceInfo: InvoiceInfo | null = needInvoiceInfoSetting(base.category)
    ? {
        settledAt: null,
        invoiceId: null,
      }
    : null;

  const externalService: IssueTaskExternalService | null =
    base.category === IssueTaskCategory.QUICK
      ? {
          service: 'dver',
          requestId: null,
          cost: null,
        }
      : null;

  const inventoryIssueInfo: IssueTaskInventoryIssueInfo | null = needInventoryIssueInfoSetting(base.category)
    ? syncInventoryIssueInfoWithProducts(null, issueCaseDoc.products ?? [])
    : null;

  // REFUND 카테고리인 경우 products와 amount를 음수로 변환
  const isRefund = base.category === IssueTaskCategory.REFUND;
  const products = isRefund ? convertProductsToNegative(issueCaseDoc.products) : issueCaseDoc.products;
  const amount = isRefund ? convertAmountToNegative(issueCaseDoc.amount) : issueCaseDoc.amount;

  return {
    pickupInfo,
    invoiceInfo,
    requestInfo: null,
    status: IssueTaskStatus.PENDING,
    registeredAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
    finishedAt: null,
    targetDate: null,
    issueCaseId: issueCaseDoc._id,
    caseSourceType: issueCaseDoc.sourceType,
    caseSourceId: issueCaseDoc.sourceId,
    caseSourceReferenceDate: issueCaseDoc.sourceReferenceDate,
    storeId: issueCaseDoc.storeId,
    storeName: issueCaseDoc.storeName,
    creatorId: base.creatorId,
    creatorType: base.creatorType,
    category: base.category,
    publicNote: null,
    privateNote: null,
    products,
    deadline,
    deliveryInfo,
    externalService,
    amount,
    linkedTaskId: null,
    inventoryIssueInfo,
    bigquerySavedAt: null,
  };
};

/**
 * PackingSheet에서 표시하는 배송 또는 픽업관련 매장의 특성을 반환합니다.
 */
export const getIssueTaskStoreFeature = (issueTaskDoc: Pick<IssueTask, 'category' | 'deliveryInfo' | 'pickupInfo'>) => {
  const { category, deliveryInfo, pickupInfo } = issueTaskDoc;
  if (category === IssueTaskCategory.DELIVERY) return deliveryInfo?.memo ?? null;
  if (category === IssueTaskCategory.PICK_UP) return pickupInfo?.memo ?? null;
  return null;
};

/**
 * TaskSpots에 들어가는 issueTask를 반환합니다.
 * Partners의 TaskItem을 생성하기 위해 상품이 필수값이다.
 *
 * @param date - 대상 날짜 (YYYY-MM-DD)
 * @param preview - 미리보기 모드 여부. true일 경우 deadline.option이 SPECIFIC_DATE이고
 *                  deadline.value가 date인 PENDING/OPEN 상태의 태스크도 포함
 */
export const getIssueTasksForTaskSpots = async (date: string, preview = false): Promise<IssueTaskDoc[]> => {
  // 확정된 태스크: IN_PROGRESS 상태이면서 targetDate가 일치하는 경우
  const confirmedTasks = await getIssueTasks([
    ['status', '==', IssueTaskStatus.IN_PROGRESS],
    ['targetDate', '==', date],
  ]);

  let previewTasks: IssueTaskDoc[] = [];
  if (preview) {
    // 미리보기: deadline.value로 쿼리하고 option, status는 로컬에서 필터링
    const candidateTasks = await getIssueTasks([['deadline.value', '==', date]]);
    previewTasks = candidateTasks.filter(
      (t) =>
        // SPECIFIC_DATE 또는 BEFORE_DATE 옵션만 포함
        (t.deadline?.option === IssueTaskDeadlineOption.SPECIFIC_DATE ||
          t.deadline?.option === IssueTaskDeadlineOption.BEFORE_DATE) &&
        // 아직 확정되지 않은 상태(PENDING, OPEN)만 포함
        (t.status === IssueTaskStatus.PENDING || t.status === IssueTaskStatus.OPEN),
    );
  }

  // 중복 제거 후 카테고리 필터링
  const uniqueTasks = uniqBy([...confirmedTasks, ...previewTasks], '_id');

  return uniqueTasks.filter((issueTask) => issueTaskCategoriesForTaskSpots.includes(issueTask.category));
};

/**
 * PaperInvoice(거래명세서)에 들어가는 issueTask의 카테고리
 * QUICK, INTERNAL을 제외한 나머지 카테고리
 * 수행중이거나 완료된 경우만 포함
 */
export const filterIssueTasksForPaperInvoice = (issueTasks: IssueTaskDoc[]): IssueTaskDoc[] => {
  return issueTasks.filter((issueTask) => {
    // QUICK 카테고리는 제외
    if ([IssueTaskCategory.QUICK, IssueTaskCategory.INTERNAL].includes(issueTask.category)) return false;

    // 수행중이거나 완료된 경우만 포함
    return issueTaskStatusForTargetDate.includes(issueTask.status);
  });
};

/**
 * task의 상태를 COMPLETED 상태로 변경 시 targetDate 검증
 */
export const validateTargetDateForCompletion = (status: IssueTaskStatus, targetDate: string | null) => {
  if (status === IssueTaskStatus.COMPLETED && !targetDate) {
    return {
      isValid: false,
      errorMessage: '완료 상태로 변경하려면 적용 날짜를 먼저 설정해야 합니다.',
    };
  }
  return { isValid: true, errorMessage: null };
};

/**
 * task의 상태를 CANCELED 상태로 변경 시 검증
 *
 * @remarks
 * - DELIVERY/PICK_UP 카테고리: Partners task 동기화는 updateIssueTaskWithCaseSync에서 자동 처리
 * - REFUND/ADDITIONAL_CHARGE 카테고리 + invoiceId 존재: invoice의 deletedAt이 null이 아니어야 함
 */
export const validateCanceledStatusForIssueTask = async (
  issueTaskDoc: IssueTaskDoc,
): Promise<{ isValid: boolean; errorMessage?: string }> => {
  const { category, invoiceInfo } = issueTaskDoc;

  // REFUND 또는 ADDITIONAL_CHARGE 카테고리 검증
  if (
    (category === IssueTaskCategory.REFUND || category === IssueTaskCategory.ADDITIONAL_CHARGE) &&
    invoiceInfo?.invoiceId
  ) {
    try {
      const invoice = await getInvoice(invoiceInfo.invoiceId);

      if (!invoice) {
        return {
          isValid: false,
          errorMessage: '청구서를 찾을 수 없습니다.',
        };
      }

      if (invoice.deletedAt === null) {
        return {
          isValid: false,
          errorMessage: '청구서를 먼저 삭제해야 합니다.',
        };
      }
    } catch (error) {
      console.error('invoice 조회 실패:', error);
      return {
        isValid: false,
        errorMessage: '청구서 조회 중 오류가 발생했습니다.',
      };
    }
  }

  return { isValid: true };
};
